/**
 * Utility functions for the search component
 *
 * Copyright (c) 2019 Propellant Software Corp
 * Author: Kelvin Nguyen
 */
import queryString from 'query-string'
import {
  BOARDS,
  BOARDS_HEADING,
  USERS,
  USERS_HEADING,
  DESTINATIONS,
  DESTINATIONS_HEADING
} from './enums/indexNames'
import { getBoardRoute, getCityDestinationUrl } from 'common/configs/routeMap'

/**
 * Return the url for the provided search option
 * @param {Object} option The search result option
 * @param {Object} user The current authenticated user
 */
export const getSearchOptionUrl = (option, user = {}) => {
  const { _source, _index } = option

  switch (_index) {
    case BOARDS: {
      const { uid } = user
      const { bid, sharedBoardId, owner } = _source
      const isOwnerOrCollaborator = (uid && owner === uid) // TODO: Fix this check when the latest User-Boards ACL is made
      const routeOptions = (isOwnerOrCollaborator) ? { boardId: bid } : { sharedBoardId }

      return getBoardRoute(routeOptions)
    }
    case USERS: {
      const { username } = _source

      return `/${username}`
    }
    case DESTINATIONS: {
      const gpid = _source.place_id

      return getCityDestinationUrl(gpid)
    }
    default:
      return '/'
  }
}

/**
 * Converts a search result index to the properly formatted option
 * @param {Object} option The search result option
 */
export const mapIndexToSearchOption = (option, user) => {
  const { _source, _index } = option

  switch (_index) {
    case BOARDS:
      return {
        value: _source.bid,
        label: _source.title,
        url: getSearchOptionUrl(option, user),
        data: option
      }
    case USERS:
      return {
        value: _source.uid,
        label: _source.username,
        url: getSearchOptionUrl(option, user),
        data: option
      }
    case DESTINATIONS:
      return {
        value: _source.place_id,
        label: _source.description,
        url: getSearchOptionUrl(option, user),
        data: option
      }
    default:
      return null
  }
}

/**
 * Converts search result indexes to the properly formatted options
 * @param {Array} indexes The search result options array
 */
export const mapIndexesToSearchOptions = (indexes, user) => {
  let options = []

  if (indexes) {
    options = indexes.map((searchOption) => (
      mapIndexToSearchOption(searchOption, user)
    )).filter((value) => value)
  }

  return options
}

/**
 * Converts search result indexes to the properly formatted grouped options
 * @param {Array} indexes The search result options array
 */
export const mapIndexesToGroupedOptions = (indexes, user) => {
  const groups = {}
  mapIndexesToSearchOptions(indexes, user)
    .forEach((option) => {
      const { data } = option
      const { _index } = data

      if (_index && !groups[_index]) {
        let label
        switch (_index) {
          case BOARDS:
            label = BOARDS_HEADING
            break
          case USERS:
            label = USERS_HEADING
            break
          case DESTINATIONS:
            label = DESTINATIONS_HEADING
            break
        }

        if (label) {
          groups[_index] = {
            label,
            options: []
          }
        }
      }

      switch (_index) {
        case BOARDS:
        case USERS:
        case DESTINATIONS:
          groups[_index].options.push(option)
      }
    })

  const indexOrder = [DESTINATIONS, USERS, BOARDS]
  return indexOrder
    .map((index) => groups[index])
    .filter((value) => value)
}

/**
 * Retrieve the search query from the current url string
 */
export const getSearchQueryFromUrl = () => {
  const queryParams = queryString.parse(window.location.search)

  return queryParams.q || ''
}

export default {
  getSearchOptionUrl,
  mapIndexToSearchOption,
  getSearchQueryFromUrl
}
